import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  FileText, 
  Search, 
  ShieldAlert, 
  AlertTriangle, 
  Info, 
  CheckCircle2, 
  X, 
  Download, 
  Filter,
  Clock,
  Globe,
  Monitor
} from 'lucide-react';
import { getAuditLogs } from '@/utils/AuditLogManager';

export default function AuditLogView({ globalSearch = '' }) {
  const [logs, setLogs] = useState(() => getAuditLogs());
  const [severityFilter, setSeverityFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState(globalSearch);
  const [selectedLog, setSelectedLog] = useState(null);

  const filteredLogs = logs.filter(l => {
    const matchesSearch = 
      l.actor.toLowerCase().includes(searchQuery.toLowerCase()) ||
      l.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
      l.target.toLowerCase().includes(searchQuery.toLowerCase()) ||
      l.ip.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesSeverity = severityFilter === 'ALL' || l.severity === severityFilter;
    return matchesSearch && matchesSeverity;
  });

  const infoCount = logs.filter(l => l.severity === 'INFO').length;
  const warningCount = logs.filter(l => l.severity === 'WARNING').length;
  const criticalCount = logs.filter(l => l.severity === 'CRITICAL').length;

  return (
    <div className="p-6 space-y-6 font-sans bg-[#F8F8F6] min-h-screen text-slate-800">
      
      {/* ─── HEADER BAR ─── */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-5 rounded-2xl border border-[#ECECEC] shadow-xs">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-black text-slate-900 tracking-tight">Audit Log</h1>
            <span className="text-xs font-bold text-[#A97838] bg-[#F4EFE6] px-2.5 py-0.5 rounded-full">Enterprise Security Audit</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Catatan audit riwayat aktivitas penting, perubahan role, reset password, & pemicu keamanan.</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400" />
            <Input 
              placeholder="Cari aktor, aksi, target, IP..." 
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-8 h-9 text-xs bg-slate-50 border-[#ECECEC] rounded-xl"
            />
          </div>

          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className="h-9 text-xs bg-slate-50 border-[#ECECEC] rounded-xl w-[130px] font-semibold">
              <SelectValue placeholder="Severity" />
            </SelectTrigger>
            <SelectContent className="bg-white text-xs">
              <SelectItem value="ALL">Semua Severity</SelectItem>
              <SelectItem value="INFO">INFO (Normal)</SelectItem>
              <SelectItem value="WARNING">WARNING (Peringatan)</SelectItem>
              <SelectItem value="CRITICAL">CRITICAL (Bahaya)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ─── 3 SEVERITY STAT CARDS ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border-[#ECECEC] shadow-xs">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-blue-600">INFO LOGS</p>
                <h3 className="text-3xl font-black text-slate-900 mt-1">{infoCount}</h3>
              </div>
              <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                <Info className="w-5 h-5" />
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Aktivitas standar pengguna</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#ECECEC] shadow-xs">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#F4B740]">WARNING ALERTS</p>
                <h3 className="text-3xl font-black text-slate-900 mt-1">{warningCount}</h3>
              </div>
              <div className="p-2 bg-amber-50 text-[#F4B740] rounded-xl">
                <AlertTriangle className="w-5 h-5" />
              </div>
            </div>
            <p className="text-[11px] text-amber-700 font-bold mt-2">Ganti password, login gagal</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#ECECEC] shadow-xs">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-rose-600">CRITICAL TRIGGERS</p>
                <h3 className="text-3xl font-black text-slate-900 mt-1">{criticalCount}</h3>
              </div>
              <div className="p-2 bg-rose-50 text-rose-600 rounded-xl">
                <ShieldAlert className="w-5 h-5" />
              </div>
            </div>
            <p className="text-[11px] text-rose-600 font-bold mt-2">Force logout, akun suspend</p>
          </CardContent>
        </Card>
      </div>

      {/* ─── AUDIT LOG TABLE ─── */}
      <Card className="bg-white border-[#ECECEC] shadow-xs overflow-hidden">
        <div className="p-4 border-b border-[#ECECEC] flex justify-between items-center">
          <h3 className="font-bold text-sm text-slate-900">Riwayat Kejadian Keamanan (Audit Logs)</h3>
          <span className="text-xs text-slate-500 font-semibold">Menampilkan {filteredLogs.length} kejadian</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-[#FAF8F5] text-slate-600 font-bold border-b border-[#ECECEC]">
              <tr>
                <th className="py-3.5 px-4 text-center">Severity</th>
                <th className="py-3.5 px-4">Waktu</th>
                <th className="py-3.5 px-4">Aktor & Role</th>
                <th className="py-3.5 px-4">Aksi / Event</th>
                <th className="py-3.5 px-4">Target Account</th>
                <th className="py-3.5 px-4">IP & Lokasi</th>
                <th className="py-3.5 px-4">Device</th>
                <th className="py-3.5 px-4 text-center">Hasil</th>
                <th className="py-3.5 px-4 text-center">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ECECEC]">
              {filteredLogs.map(l => (
                <tr key={l.id} className="hover:bg-[#FAF8F5] transition-colors">
                  <td className="py-3.5 px-4 text-center">
                    <span className={`font-mono text-[9px] font-extrabold px-2.5 py-0.5 rounded-full border ${
                      l.severity === 'CRITICAL' ? 'bg-rose-50 text-rose-600 border-rose-200' :
                      l.severity === 'WARNING' ? 'bg-amber-50 text-[#F4B740] border-amber-200' :
                      'bg-blue-50 text-blue-600 border-blue-200'
                    }`}>
                      {l.severity}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 font-mono text-slate-500 text-[11px]">
                    {new Date(l.timestamp).toLocaleTimeString('id-ID')}
                  </td>
                  <td className="py-3.5 px-4">
                    <p className="font-bold text-slate-900">{l.actor}</p>
                    <p className="text-[10px] text-slate-400 font-semibold">{l.role}</p>
                  </td>
                  <td className="py-3.5 px-4 font-bold text-[#A97838]">{l.action}</td>
                  <td className="py-3.5 px-4 font-semibold text-slate-800">{l.target}</td>
                  <td className="py-3.5 px-4 font-mono text-slate-600 text-[11px]">{l.ip} ({l.country})</td>
                  <td className="py-3.5 px-4 text-slate-500 text-[11px]">{l.device}</td>
                  <td className="py-3.5 px-4 text-center">
                    <span className="font-bold text-[10px] bg-emerald-50 text-[#20C05C] px-2 py-0.5 rounded border border-emerald-200">
                      {l.result}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 text-center">
                    <button 
                      onClick={() => setSelectedLog(l)}
                      className="text-[11px] font-bold text-[#A97838] hover:underline"
                    >
                      Lihat Detail
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ─── MODAL AUDIT LOG DETAIL ─── */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 -xs p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-xl border border-[#ECECEC]">
            <div className="flex justify-between items-center border-b border-[#ECECEC] pb-3">
              <h3 className="font-black text-slate-900 text-sm flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#A97838]" /> Detail Kejadian Audit Log
              </h3>
              <button onClick={() => setSelectedLog(null)} className="p-1 text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-2.5 text-xs">
              <div className="flex justify-between"><span className="text-slate-400">ID Log</span><span className="font-mono font-bold text-slate-800">{selectedLog.id}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Waktu Kejadian</span><span className="font-mono font-bold text-slate-800">{selectedLog.timestamp}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Severity</span><span className="font-bold text-rose-600">{selectedLog.severity}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Pelaku (Actor)</span><span className="font-bold text-slate-900">{selectedLog.actor}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Target</span><span className="font-semibold text-slate-800">{selectedLog.target}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">IP & Perangkat</span><span className="font-mono text-slate-600">{selectedLog.ip} • {selectedLog.device}</span></div>
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl mt-3 text-slate-700">
                <p className="font-bold text-[11px] text-slate-500 mb-1">Keterangan Lengkap:</p>
                <p className="text-xs leading-relaxed">{selectedLog.details}</p>
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <Button onClick={() => setSelectedLog(null)} className="bg-[#302621] text-white font-bold h-9 text-xs rounded-xl">
                Tutup
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
